import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import { CountingDots } from './types';
import { NUMERAL_FONT_STYLE } from '../../PreKAssessment/preKConstants';
import {
  PracticeHeader,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  generatePracticeAnswerChoices,
  PREK_PRACTICE_TRACKS,
  PRACTICE_VISUAL_CONFIG,
} from '../shared';

interface WeDoScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}

const TIMING = {
  FLASH_DURATION_MS: 700,
  PAUSE_BETWEEN_DOTS_MS: 200,
  SHOW_ANSWERS_DELAY_MS: 300,
};

const WeDoScreen: React.FC<WeDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedQueue, setFailedQueue] = useState<{ factId: string; digit: number }[]>([]);
  const [isRetryPhase, setIsRetryPhase] = useState(false);
  const [currentRetryIndex, setCurrentRetryIndex] = useState(0);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [highlightCorrect, setHighlightCorrect] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());
  const [currentPattern, setCurrentPattern] = useState<CountingDots | null>(null);

  // Counting animation state
  const [flashingDotIndex, setFlashingDotIndex] = useState<number | null>(null);
  const [showAnswerChoices, setShowAnswerChoices] = useState(false);

  // Sequence control
  const sequenceIdRef = useRef(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const { playTTSWithCallback, playChime, isTTSPlaying } = usePreKTTS();

  // Clear all pending timeouts
  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(t => clearTimeout(t));
    timeoutsRef.current = [];
  }, []);

  // Helper to add a tracked timeout
  const addTimeout = useCallback((callback: () => void, delay: number) => {
    const t = setTimeout(callback, delay);
    timeoutsRef.current.push(t);
    return t;
  }, []);

  // Generate dot pattern for given quantity
  const generateCountingDots = useCallback((quantity: number): CountingDots => {
    const gridPositions = PRACTICE_VISUAL_CONFIG.COUNTING_GRID;

    // Randomly select which positions to use
    const shuffledPositions = [...gridPositions].sort(() => Math.random() - 0.5);
    const selectedPositions = shuffledPositions.slice(0, quantity);

    // Sort in reading order: top-to-bottom, then left-to-right
    selectedPositions.sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });

    const dots = selectedPositions.map((pos, i) => ({
      x: pos.x,
      y: pos.y,
      id: `dot-${i}`
    }));

    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots
    };
  }, []);

  // Get current fact
  const getCurrentFact = useCallback(() => {
    if (isRetryPhase) {
      return failedQueue[currentRetryIndex];
    }
    return factsToPractice[currentFactIndex];
  }, [isRetryPhase, failedQueue, currentRetryIndex, factsToPractice, currentFactIndex]);

  const currentFact = getCurrentFact();
  const currentQuantity = currentFact?.digit;

  // Count a single dot: flash it, play audio, then call next
  const countDot = useCallback((
    dotIndex: number,
    quantity: number,
    seqId: number,
    onAllDone: () => void
  ) => {
    if (sequenceIdRef.current !== seqId) return;

    if (dotIndex >= quantity) {
      onAllDone();
      return;
    }

    // Highlight this dot
    setFlashingDotIndex(dotIndex);

    // Get the audio for this number (1-indexed)
    const numberAudio = QUESTION_SCREEN_AUDIO.countingNumbers[(dotIndex + 1) as keyof typeof QUESTION_SCREEN_AUDIO.countingNumbers];

    const proceedToNext = () => {
      if (sequenceIdRef.current !== seqId) return;

      // Remove highlight after flash duration
      addTimeout(() => {
        if (sequenceIdRef.current !== seqId) return;
        setFlashingDotIndex(null);

        // Brief pause then count next dot
        addTimeout(() => {
          countDot(dotIndex + 1, quantity, seqId, onAllDone);
        }, TIMING.PAUSE_BETWEEN_DOTS_MS);
      }, TIMING.FLASH_DURATION_MS);
    };

    if (settings.speechEnabled && numberAudio) {
      playTTSWithCallback(numberAudio, proceedToNext).catch(proceedToNext);
    } else {
      addTimeout(proceedToNext, 500);
    }
  }, [settings.speechEnabled, playTTSWithCallback, addTimeout]);

  // Run the counting sequence
  const runSequence = useCallback((pattern: CountingDots, seqId: number) => {
    const quantity = pattern.quantity;

    // Reset visual state
    setFlashingDotIndex(null);
    setShowAnswerChoices(false);
    setHasAnswered(false);
    setHighlightCorrect(false);
    setCorrectAnswerGiven(false);

    const startCounting = () => {
      if (sequenceIdRef.current !== seqId) return;

      // Count each dot one by one
      countDot(0, quantity, seqId, () => {
        if (sequenceIdRef.current !== seqId) return;

        // All dots counted - play "Now tap the answer" then show choices
        const showChoices = () => {
          if (sequenceIdRef.current !== seqId) return;

          addTimeout(() => {
            if (sequenceIdRef.current !== seqId) return;
            setShowAnswerChoices(true);

            // Brief highlight of correct answer
            addTimeout(() => {
              if (sequenceIdRef.current !== seqId) return;
              setHighlightCorrect(true);
              addTimeout(() => {
                setHighlightCorrect(false);
              }, PRACTICE_TIMER_CONSTANTS.WE_DO_HIGHLIGHT_MS);
            }, 300);
          }, TIMING.SHOW_ANSWERS_DELAY_MS);
        };

        if (settings.speechEnabled) {
          playTTSWithCallback(QUESTION_SCREEN_AUDIO.countingPhrases.nowTap, showChoices)
            .catch(showChoices);
        } else {
          showChoices();
        }
      });
    };

    // Play intro "Count with me"
    if (settings.speechEnabled) {
      playTTSWithCallback(QUESTION_SCREEN_AUDIO.countingPhrases.countWithMe, startCounting)
        .catch(startCounting);
    } else {
      addTimeout(startCounting, 500);
    }
  }, [settings.speechEnabled, playTTSWithCallback, countDot, addTimeout]);

  // Initialize pattern and start sequence when fact changes
  useEffect(() => {
    if (currentQuantity === undefined) return;

    // Cancel any running sequence
    sequenceIdRef.current += 1;
    const currentSeqId = sequenceIdRef.current;
    clearAllTimeouts();

    // Generate new pattern and answer choices
    const pattern = generateCountingDots(currentQuantity);
    setCurrentPattern(pattern);
    setAnswerChoices(generatePracticeAnswerChoices(PREK_PRACTICE_TRACKS.COUNTING, currentQuantity));

    // Reset state
    setFlashingDotIndex(null);
    setShowAnswerChoices(false);
    setHasAnswered(false);
    setHighlightCorrect(false);
    setCorrectAnswerGiven(false);

    // Start sequence after brief delay
    const startTimer = setTimeout(() => {
      runSequence(pattern, currentSeqId);
    }, 100);

    return () => {
      clearTimeout(startTimer);
    };
  }, [currentFactIndex, currentRetryIndex, isRetryPhase, currentQuantity,
      generateCountingDots, clearAllTimeouts, runSequence]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      sequenceIdRef.current += 1;
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  const advanceToNext = useCallback(() => {
    if (isRetryPhase) {
      if (failedQueue.length === 0) {
        onComplete();
      } else if (currentRetryIndex >= failedQueue.length) {
        setCurrentRetryIndex(0);
      } else if (currentRetryIndex < failedQueue.length - 1) {
        setCurrentRetryIndex(currentRetryIndex + 1);
      } else {
        setCurrentRetryIndex(0);
      }
    } else {
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        if (failedQueue.length > 0) {
          setIsRetryPhase(true);
          setCurrentRetryIndex(0);
        } else {
          onComplete();
        }
      }
    }
  }, [isRetryPhase, currentRetryIndex, failedQueue.length, currentFactIndex, factsToPractice.length, onComplete]);

  const handleAnswerClick = useCallback((selectedQuantity: number) => {
    if (isTTSPlaying || hasAnswered || !showAnswerChoices) return;

    setHasAnswered(true);
    const isCorrect = selectedQuantity === currentQuantity;

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore + 1;
        newScores.set(currentFact.factId, newScore);

        if (currentScore === 0 && newScore === 1) {
          setCompletedCount(prevCount => prevCount + 1);
        }

        return newScores;
      });

      if (isRetryPhase) {
        setFailedQueue(prev => {
          const newQueue = prev.filter((_, index) => index !== currentRetryIndex);
          if (newQueue.length === 0) {
            setTimeout(() => onComplete(), 600);
          } else {
            setTimeout(() => {
              if (currentRetryIndex >= newQueue.length) {
                setCurrentRetryIndex(0);
              }
            }, 600);
          }
          return newQueue;
        });
      }

      playChime().then(() => {
        if (!isRetryPhase) {
          setTimeout(() => {
            advanceToNext();
          }, 500);
        }
      });
    } else {
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore - 1;
        newScores.set(currentFact.factId, newScore);

        if (currentScore >= 1 && newScore < 1) {
          setCompletedCount(prevCount => prevCount - 1);
        }

        return newScores;
      });

      if (!correctAnswerGiven) {
        if (!isRetryPhase) {
          setFailedQueue(prev => {
            const alreadyExists = prev.some(f => f.factId === currentFact.factId);
            if (!alreadyExists) {
              return [...prev, currentFact];
            }
            return prev;
          });
        } else {
          const failedFact = failedQueue[currentRetryIndex];
          setFailedQueue(prev => [...prev.filter((_, index) => index !== currentRetryIndex), failedFact]);
        }
      }

      setTimeout(() => {
        setHasAnswered(false);
      }, PRACTICE_TIMER_CONSTANTS.WRONG_ANSWER_RETRY_MS);
    }
  }, [currentQuantity, hasAnswered, isTTSPlaying, showAnswerChoices, playChime,
      correctAnswerGiven, isRetryPhase, currentFact, failedQueue, currentRetryIndex, advanceToNext, onComplete]);

  // Loading state
  if (currentQuantity === undefined || !currentPattern) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.WE_DO}
        onBack={onBack}
        progress={completedCount / factsToPractice.length}
      />

      {/* Main Content Area - Side-by-side layout */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-6xl flex items-center justify-center gap-24">

          {/* Left Side: Dots Container - 4x8 grid */}
          <div className="flex-shrink-0" style={{ width: '200px', height: '400px' }}>
            <div className="relative w-full h-full">
              {currentPattern.dots.map((dot, index) => {
                const isFlashing = flashingDotIndex === index;

                return (
                  <motion.div
                    key={dot.id}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                      scale: isFlashing ? 1.3 : 1,
                      opacity: 1,
                    }}
                    transition={{
                      scale: { duration: 0.25, ease: 'easeOut' },
                      opacity: { duration: 0.3, ease: 'easeOut' },
                    }}
                    className="absolute w-6 h-6 lg:w-8 lg:h-8 rounded-full shadow-lg"
                    style={{
                      left: `${dot.x}%`,
                      top: `${dot.y}%`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: isFlashing ? '#facc15' : '#2563eb',
                      boxShadow: isFlashing
                        ? '0 0 16px 6px rgba(250, 204, 21, 0.5), 0 0 32px 12px rgba(250, 204, 21, 0.25)'
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Right Side: Answer Choices */}
          <div className="w-full max-w-md lg:max-w-lg">
            {showAnswerChoices ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-2 gap-8 lg:gap-12"
              >
                {answerChoices.map((choice, index) => (
                  <motion.button
                    key={choice}
                    initial={{ opacity: 0, y: 30, rotate: -5 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      rotate: 0,
                      scale: highlightCorrect && choice === currentQuantity ? [1, 1.1, 1] : 1,
                      boxShadow: highlightCorrect && choice === currentQuantity ?
                        ['0 0 0 0 rgba(34, 197, 94, 0.7)', '0 0 0 20px rgba(34, 197, 94, 0)', '0 0 0 0 rgba(34, 197, 94, 0)'] :
                        undefined
                    }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.1,
                      scale: { duration: highlightCorrect && choice === currentQuantity ? 2 : 0.4, repeat: highlightCorrect && choice === currentQuantity ? 1 : 0 }
                    }}
                    whileHover={!hasAnswered && !isTTSPlaying ? { scale: 1.08, rotate: 1 } : {}}
                    whileTap={!hasAnswered && !isTTSPlaying ? { scale: 0.92 } : {}}
                    onClick={() => handleAnswerClick(choice)}
                    disabled={hasAnswered || isTTSPlaying}
                    className={`
                      h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                      ${highlightCorrect && choice === currentQuantity
                        ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300 animate-pulse'
                        : hasAnswered && choice === currentQuantity
                        ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                        : hasAnswered || isTTSPlaying
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl transform-gpu'
                      }
                      ${hasAnswered || isTTSPlaying ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
                    `}
                    style={NUMERAL_FONT_STYLE}
                  >
                    {choice}
                  </motion.button>
                ))}
              </motion.div>
            ) : (
              // Empty placeholder to maintain layout during counting
              <div className="h-32 lg:h-40" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeDoScreen;
